import {
    BaseOutboundLoadTemplateDefinition, OutboundLoadTemplateSharedContext,
    OutboundLoadTemplate,
    OutboundLoadTemplateLocalContext,
    OutboundLoadTemplateDefinition,
    OutboundLoadTemplateFormControls
} from "./base";
import {Observable, Subject} from "rxjs";
import {Carrier} from "@shared/models/carrier";
import {Customer} from "@shared/models/customer";
import {OutboundLoadService} from "../../../../../services/outbound-load.service";
import {CarrierService} from "@shared/services/carrier.service";
import {CustomerService} from "@shared/services/customer.service";
import {map} from "rxjs/operators";
import {Cacheable} from "ts-cacheable";
import {FormGroup} from "@angular/forms";
import {OutboundLoadCreate} from "../../../../../models/outbound-load";
import {toTimestamp} from "../../../../../helpers/to-timestamp";
import {SubinventoryType} from "@shared/models/subinventory";
import {OutboundLoadStatus} from "../../../../../models/outbound-load-status";

export class WalmartReuseTemplate extends BaseOutboundLoadTemplateDefinition implements OutboundLoadTemplateDefinition {
    label = 'Walmart Reuse';

    private static readonly carrierCache = new Subject<void>();
    private static readonly poolerCache = new Subject<void>();

    constructor(
        private carrierService: CarrierService,
        private customerService: CustomerService,
        private outboundLoadService: OutboundLoadService
    ) {
        super();
    }

    @Cacheable({
        cacheBusterObserver: WalmartReuseTemplate.carrierCache,
        maxAge: 5 * 60 * 1000
    })
    getWalmartCarrier(): Observable<Carrier> {
        return this.carrierService.getCarrier('WALM');
    }

    @Cacheable({
        cacheBusterObserver: WalmartReuseTemplate.poolerCache,
        maxAge: 5 * 60 * 1000
    })
    getWalmartPooler(): Observable<Customer> {
        return this.customerService.findCustomers({ accountNumber: 'WALM001' }).pipe(
            map(([first]) => first ?? null)
        );
    }

    toOutboundLoadModel(formGroup: FormGroup<OutboundLoadTemplateFormControls>, sharedContext: OutboundLoadTemplateSharedContext, localContext: OutboundLoadTemplateLocalContext): OutboundLoadCreate {
        const hasShipped = formGroup.get('hasShipped')?.value;

        return {
            siteId: sharedContext.shiftContext.site.id,
            siteSubinventoryId: formGroup.get('siteSubinventory')?.value?.id,
            loadedWorkedShiftId: sharedContext.shiftContext.id,
            loadedTimestamp: toTimestamp(
                formGroup.get('loadDate')?.value,
                formGroup.get('loadTime').value,
                sharedContext.siteTimezone
            ),
            propakBOLNumber: formGroup.get('propakBOLNumber')?.value,
            sealNumber: '',
            carrierId: formGroup.get('carrier')?.value?.id,
            carrierName: formGroup.get('carrier')?.value?.carrierName,
            trailerId: formGroup.get('trailerId')?.value,
            outboundLoadStatusId: hasShipped ? OutboundLoadStatus.Shipped : OutboundLoadStatus.Loaded,
            customerNeededTimestamp: null,
            scheduledPickupTimestamp: hasShipped
                ? toTimestamp(
                    formGroup.get('scheduledPickupDate')?.value,
                    formGroup.get('scheduledPickupTime')?.value,
                    sharedContext.siteTimezone)
                : null,
            shippedWorkedShiftId: hasShipped
                ? formGroup.get('shippedShiftId')?.value
                : null,
            shippedTimestamp: hasShipped
                ? toTimestamp(
                    formGroup.get('dateShipped')?.value,
                    formGroup.get('timeShipped')?.value,
                    sharedContext.siteTimezone)
                : null,
            customerReferenceNumber: '',
            altReferenceNumber: '',
            isRejected: false,
            rejectionReason: '',
            stockTransfer: false,
            isReuse: true,
            outboundLoadLineItems: [
                {
                    productId: formGroup.get('product')?.value?.id,
                    quantity: formGroup.get('quantity').value,
                    isScanned: false,
                    isUnderload: false,
                }
            ],
            outboundLoadExceptions: [],
            canceledWorkedShiftId: null,
            customerId: formGroup.get('pooler')?.value?.id,
            poolerId: formGroup.get('pooler')?.value?.id,
            customerSiteId: formGroup.get('shipToLocation')?.value?.id,
        };
    }

    transformContext(context: OutboundLoadTemplateLocalContext): OutboundLoadTemplateLocalContext {
        const allowedProducts = new Set(['4055 RU PLUS Reuse', '4055 RU PLUS REUSE WA']);

        return {
            ...context,
            products: context.products.filter(p => allowedProducts.has(p.itemNumber))
        };
    }

    get template(): OutboundLoadTemplate {
        return {
            loadDate: {
                defaultValueFn: (context: OutboundLoadTemplateSharedContext) => context.shiftContext.date.toJSDate(),
                isVisible: true,
            },
            loadTime: {
                isVisible: true
            },
            trailerId: {
                isVisible: true
            },
            carrier: {
                defaultValueFn: () => this.getWalmartCarrier(),
                isVisible: false
            },
            shipToLocation: {
                isVisible: true,
            },
            invoiceTo: {
                defaultValueFn: () => this.getWalmartPooler(),
                isVisible: false,
            },
            pooler: {
                defaultValueFn: () => this.getWalmartPooler(),
                isVisible: false
            },
            propakBOLNumber: {
                defaultValueFn: (context: OutboundLoadTemplateSharedContext) =>
                    this.outboundLoadService.getNextBolNumber(context.shiftContext.site.id),
                isVisible: false,
            },
            siteSubinventory: {
                isVisible: false,
                defaultValueFn: (context: OutboundLoadTemplateSharedContext) =>
                    context.siteSubInventories.find(subInv => subInv.subinventoryType === SubinventoryType.TpmProduction)
            },
            product: {
                isVisible: true,
            },
            quantity: {
                isVisible: true
            },
            hasShipped: {
                isVisible: true
            },
            scheduledPickupDate: {
                isVisible: true,
                defaultValueFn: (context: OutboundLoadTemplateSharedContext) =>
                    context.shiftContext?.date?.toJSDate()
            },
            scheduledPickupTime: {
                isVisible: true,
            },
            dateShipped: {
                isVisible: true,
                defaultValueFn: (context: OutboundLoadTemplateSharedContext) =>
                    context.shiftContext?.date?.toJSDate()
            },
            timeShipped: {
                isVisible: true,
            },
            shippedShiftId: {
                isVisible: true,
                defaultValueFn: (context: OutboundLoadTemplateSharedContext) =>
                    context.shiftContext?.id
            }
        };
    }
}
